import { RUNTIME_BACKEND_URL } from '../../lib/backendUrl';
import { useEffect, useState, useCallback } from 'react';
import { useHesapAuth } from '../../store/hesapStore';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Loader2, ChefHat, Clock, CheckCircle2, Truck, TrendingUp,
  RefreshCw, Plus, Edit2, Trash2, X, Printer
} from 'lucide-react';
import { useReceiptPrinter } from '../../components/ReceiptPrinter';

const API = `${RUNTIME_BACKEND_URL}/api/hesap`;

const resizeImage = (file, maxW = 400, maxH = 400) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let w = img.width, h = img.height;
        if (w > maxW) { h = h * maxW / w; w = maxW; }
        if (h > maxH) { w = w * maxH / h; h = maxH; }
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/jpeg', 0.7));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
};

export default function HesapYemekMutfakPage() {
  const { token } = useHesapAuth();
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const [tab, setTab] = useState('orders');
  const [orders, setOrders] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [reports, setReports] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMenuDialog, setShowMenuDialog] = useState(false);
  const [editingMenu, setEditingMenu] = useState(null);
  const [saving, setSaving] = useState(false);
  const [reportPeriod, setReportPeriod] = useState('daily');
  const [reportDate, setReportDate] = useState(new Date().toISOString().split('T')[0]);
  const { printReceipt } = useReceiptPrinter();

  const emptyMenuForm = {
    name: '', price: '', category: '', description: '', image: '', is_active: true,
    variants: []
  };
  const [menuForm, setMenuForm] = useState(emptyMenuForm);

  const loadOrders = useCallback(async () => {
    try {
      const [r1, r2, r3] = await Promise.all([
        fetch(`${API}/food/orders?status=bekliyor`, { headers }),
        fetch(`${API}/food/orders?status=hazirlaniyor`, { headers }),
        fetch(`${API}/food/orders?status=hazir`, { headers })
      ]);
      const all = [];
      if (r1.ok) all.push(...(await r1.json()));
      if (r2.ok) all.push(...(await r2.json()));
      if (r3.ok) all.push(...(await r3.json()));
      all.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      setOrders(all);
    } catch {} finally { setLoading(false); }
  }, [token]);

  const loadMenu = async () => {
    const res = await fetch(`${API}/menu-items`, { headers });
    if (res.ok) setMenuItems(await res.json());
  };

  const loadReports = async () => {
    const res = await fetch(`${API}/food/reports?period=${reportPeriod}&date=${reportDate}`, { headers });
    if (res.ok) setReports(await res.json());
  };

  useEffect(() => { loadOrders(); loadMenu(); }, []);
  useEffect(() => { if (tab === 'reports') loadReports(); }, [tab, reportPeriod, reportDate]);
  useEffect(() => {
    if (tab === 'orders') {
      const interval = setInterval(loadOrders, 15000);
      return () => clearInterval(interval);
    }
  }, [tab, loadOrders]);

  const updateStatus = async (orderId, newStatus) => {
    try {
      const res = await fetch(`${API}/food/orders/${orderId}/status?new_status=${newStatus}`, { method: 'PUT', headers });
      if (!res.ok) throw new Error();
      const labels = { hazirlaniyor: 'Hazırlanıyor', hazir: 'Hazır', teslim: 'Teslim Edildi' };
      toast.success(labels[newStatus] || newStatus);
      loadOrders();
    } catch { toast.error('Hata'); }
  };

  const openMenuForm = (item) => {
    if (item) {
      setEditingMenu(item);
      setMenuForm({
        name: item.name, price: item.price, category: item.category || '',
        description: item.description || '', image: item.image || '', is_active: item.is_active !== false,
        variants: item.variants || []
      });
    } else {
      setEditingMenu(null);
      setMenuForm({ ...emptyMenuForm, variants: [] });
    }
    setShowMenuDialog(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const resized = await resizeImage(file, 400, 400);
      setMenuForm(prev => ({ ...prev, image: resized }));
      toast.success('Görsel yüklendi');
    } catch { toast.error('Görsel yüklenemedi'); }
  };

  const addVariant = () => {
    setMenuForm(prev => ({
      ...prev,
      variants: [...prev.variants, { label: '', price: '' }]
    }));
  };

  const updateVariant = (idx, field, value) => {
    setMenuForm(prev => ({
      ...prev,
      variants: prev.variants.map((v, i) => i === idx ? { ...v, [field]: value } : v)
    }));
  };

  const removeVariant = (idx) => {
    setMenuForm(prev => ({
      ...prev,
      variants: prev.variants.filter((_, i) => i !== idx)
    }));
  };

  const saveMenuItem = async (e) => {
    e.preventDefault();
    if (!menuForm.name.trim()) { toast.error('Ad gerekli'); return; }
    if (menuForm.variants.length === 0 && !menuForm.price) { toast.error('Fiyat gerekli'); return; }
    if (menuForm.variants.length > 0) {
      const invalid = menuForm.variants.some(v => !v.label.trim() || !v.price);
      if (invalid) { toast.error('Tüm seçenek adı ve fiyatı doldurun'); return; }
    }
    setSaving(true);
    try {
      const variants = menuForm.variants.map(v => ({ label: v.label.trim(), price: parseFloat(v.price) }));
      const basePrice = variants.length > 0 ? Math.min(...variants.map(v => v.price)) : parseFloat(menuForm.price);
      const body = {
        name: menuForm.name,
        price: basePrice,
        category: menuForm.category,
        description: menuForm.description,
        image: menuForm.image,
        is_active: menuForm.is_active,
        variants
      };
      const url = editingMenu ? `${API}/menu-items/${editingMenu.id}` : `${API}/menu-items`;
      const method = editingMenu ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers, body: JSON.stringify(body) });
      if (!res.ok) throw new Error();
      toast.success(editingMenu ? 'Güncellendi' : 'Eklendi');
      setShowMenuDialog(false);
      loadMenu();
    } catch { toast.error('Hata'); }
    finally { setSaving(false); }
  };

  const deleteMenuItem = async (item) => {
    if (!window.confirm(`${item.name} silinsin mi?`)) return;
    await fetch(`${API}/menu-items/${item.id}`, { method: 'DELETE', headers });
    toast.success('Silindi');
    loadMenu();
  };

  const statusColors = {
    bekliyor: 'bg-amber-100 border-amber-300 text-amber-800',
    hazirlaniyor: 'bg-blue-100 border-blue-300 text-blue-800',
    hazir: 'bg-green-100 border-green-300 text-green-800'
  };
  const statusLabels = { bekliyor: 'Bekliyor', hazirlaniyor: 'Hazırlanıyor', hazir: 'Hazır' };

  if (loading && tab === 'orders') return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="hesap-yemek-mutfak">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-800">{`Yemek Yönetimi`}</h1>
        <Button variant="outline" size="sm" onClick={loadOrders}><RefreshCw className="h-3.5 w-3.5 mr-1" />Yenile</Button>
      </div>

      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit">
        {[['orders', `Siparişler`, Clock], ['menu', `Menü Yönetimi`, ChefHat], ['reports', 'Raporlar', TrendingUp]].map(([key, label, Icon]) => (
          <button key={key} onClick={() => setTab(key)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition ${tab === key ? 'bg-white shadow text-slate-800' : 'text-slate-500 hover:text-slate-700'}`}>
            <Icon className="h-4 w-4" />{label}
          </button>
        ))}
      </div>

      {/* SIPARISLER */}
      {tab === 'orders' && (
        <div className="space-y-3">
          {orders.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <ChefHat className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>{`Bekleyen sipariş yok`}</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {orders.map(order => (
                <Card key={order.id} className={`border-2 ${statusColors[order.status]}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {order.table_no && <span className="font-bold text-lg">Masa {order.table_no}</span>}
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusColors[order.status]}`}>
                          {statusLabels[order.status]}
                        </span>
                      </div>
                      <span className="text-xs text-slate-500">
                        {new Date(order.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <div className="space-y-1 mb-3">
                      {order.items.map((item, i) => (
                        <div key={i} className="flex justify-between text-sm">
                          <span>{item.quantity}x {item.name}{item.variant ? ` (${item.variant})` : ''}</span>
                          <span className="text-slate-500">{(item.price * item.quantity).toLocaleString('tr-TR')} TL</span>
                        </div>
                      ))}
                    </div>
                    {order.note && <p className="text-xs text-slate-500 italic mb-2">Not: {order.note}</p>}
                    <div className="flex items-center justify-between border-t pt-2">
                      <div className="flex gap-1">
                        <Button variant="outline" size="sm" onClick={() => printReceipt(order, { showKitchen: true })} title="Mutfak Fişi" data-testid={`print-kitchen-${order.id}`}>
                          <Printer className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => printReceipt(order)} title="Sipariş Fişi" data-testid={`print-receipt-${order.id}`}>
                          <Printer className="h-3.5 w-3.5 mr-1" />Fiş
                        </Button>
                      </div>
                      <span className="font-bold">{order.total.toLocaleString('tr-TR')} TL</span>
                      <div className="flex gap-1">
                        {order.status === 'bekliyor' && (
                          <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => updateStatus(order.id, 'hazirlaniyor')}>{`Hazırla`}</Button>
                        )}
                        {order.status === 'hazirlaniyor' && (
                          <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => updateStatus(order.id, 'hazir')}>{`Hazır`}</Button>
                        )}
                        {order.status === 'hazir' && (
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => updateStatus(order.id, 'teslim')}>
                            <Truck className="h-3.5 w-3.5 mr-1" />Teslim
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* MENU YONETIMI */}
      {tab === 'menu' && (
        <div className="space-y-3">
          <div className="flex justify-end">
            <Button onClick={() => openMenuForm(null)} data-testid="add-menu-item-btn">
              <Plus className="h-4 w-4 mr-1" />{`Menü Ögesi Ekle`}
            </Button>
          </div>
          {menuItems.length === 0 ? (
            <div className="text-center py-12 text-slate-400"><p>{`Henüz menü ögesi yok`}</p></div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {menuItems.map(item => (
                <Card key={item.id} className={`overflow-hidden ${!item.is_active ? 'opacity-50' : ''}`}>
                  <div className="aspect-square bg-slate-100 relative overflow-hidden">
                    {item.image ? (
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-300 text-4xl font-bold">{item.name.charAt(0)}</div>
                    )}
                    {!item.is_active && <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-white font-bold">Pasif</div>}
                    {item.variants?.length > 0 && (
                      <div className="absolute top-2 left-2 bg-orange-500 text-white rounded px-1.5 py-0.5 text-[10px] font-medium">
                        {item.variants.length} seçenek
                      </div>
                    )}
                  </div>
                  <CardContent className="p-3">
                    <p className="font-medium text-sm truncate">{item.name}</p>
                    <p className="text-emerald-600 font-bold">{item.price.toLocaleString('tr-TR')} TL</p>
                    {item.variants?.length > 0 && (
                      <p className="text-[10px] text-orange-500 truncate">{item.variants.map(v => v.label).join(', ')}</p>
                    )}
                    {item.category && <p className="text-[10px] text-slate-400">{item.category}</p>}
                    <div className="flex gap-1 mt-2">
                      <Button variant="outline" size="sm" className="flex-1 text-xs" onClick={() => openMenuForm(item)}>
                        <Edit2 className="h-3 w-3 mr-1" />{`Düzenle`}
                      </Button>
                      <Button variant="outline" size="sm" className="text-red-500 text-xs" onClick={() => deleteMenuItem(item)}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* RAPORLAR */}
      {tab === 'reports' && (
        <div className="space-y-4">
          <Card><CardContent className="p-3 flex flex-wrap items-end gap-3">
            <div className="flex gap-1">
              {[['daily', `Günlük`], ['monthly', `Aylık`]].map(([v, l]) => (
                <button key={v} onClick={() => setReportPeriod(v)}
                  className={`px-3 py-1.5 rounded text-xs font-medium ${reportPeriod === v ? 'bg-slate-800 text-white' : 'bg-slate-100'}`}>{l}</button>
              ))}
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Tarih</Label>
              <Input type="date" value={reportDate} onChange={e => setReportDate(e.target.value)} className="h-8 w-36 text-sm" />
            </div>
            <Button variant="outline" size="sm" onClick={loadReports}><RefreshCw className="h-3.5 w-3.5 mr-1" />Yenile</Button>
          </CardContent></Card>
          {reports && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <Card><CardContent className="p-4 text-center">
                  <p className="text-xs text-slate-500">{`Toplam Satış`}</p>
                  <p className="text-xl font-bold text-emerald-600">{reports.total_revenue.toLocaleString('tr-TR')} TL</p>
                </CardContent></Card>
                <Card><CardContent className="p-4 text-center">
                  <p className="text-xs text-slate-500">{`Sipariş Sayısı`}</p>
                  <p className="text-xl font-bold text-slate-700">{reports.total_orders}</p>
                </CardContent></Card>
                <Card><CardContent className="p-4 text-center">
                  <p className="text-xs text-slate-500">{`Ortalama Sipariş`}</p>
                  <p className="text-xl font-bold text-blue-600">
                    {reports.total_orders > 0 ? (reports.total_revenue / reports.total_orders).toLocaleString('tr-TR', { maximumFractionDigits: 0 }) : 0} TL
                  </p>
                </CardContent></Card>
              </div>
              {reports.item_summary?.length > 0 && (
                <Card><CardContent className="p-4">
                  <h3 className="font-medium mb-3">{`Ürün Bazlı Satış`}</h3>
                  <div className="space-y-2">
                    {reports.item_summary.sort((a, b) => b.revenue - a.revenue).map((item, i) => (
                      <div key={i} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                        <div><p className="text-sm font-medium">{item.name}</p><p className="text-xs text-slate-400">{item.quantity} adet</p></div>
                        <span className="font-bold text-emerald-600">{item.revenue.toLocaleString('tr-TR')} TL</span>
                      </div>
                    ))}
                  </div>
                </CardContent></Card>
              )}
            </>
          )}
        </div>
      )}

      {/* Menu Item Dialog */}
      <Dialog open={showMenuDialog} onOpenChange={setShowMenuDialog}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingMenu ? `Menü Ögesi Düzenle` : `Yeni Menü Ögesi`}</DialogTitle></DialogHeader>
          <form onSubmit={saveMenuItem} className="space-y-3">
            <div className="space-y-1">
              <Label>{`Ürün Adı *`}</Label>
              <Input value={menuForm.name} onChange={e => setMenuForm({...menuForm, name: e.target.value})}
                placeholder="Adana Kebap" data-testid="menu-item-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Kategori</Label>
                <Input value={menuForm.category} onChange={e => setMenuForm({...menuForm, category: e.target.value})}
                  placeholder="Ana Yemek" />
              </div>
              <div className="space-y-1">
                <Label>{`Açıklama`}</Label>
                <Input value={menuForm.description} onChange={e => setMenuForm({...menuForm, description: e.target.value})} />
              </div>
            </div>

            {/* Seçenekler (Varyantlar) */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Seçenekler</Label>
                <Button type="button" variant="outline" size="sm" onClick={addVariant} className="text-xs h-7">
                  <Plus className="h-3 w-3 mr-1" />Seçenek Ekle
                </Button>
              </div>
              {menuForm.variants.length > 0 && (
                <div className="space-y-2 p-3 bg-orange-50 rounded-lg border border-orange-200">
                  <p className="text-[10px] text-orange-600">Her seçenek için ad ve fiyat girin (örn: 100gr → 250 TL, Sucuklu → 300 TL)</p>
                  {menuForm.variants.map((v, idx) => (
                    <div key={idx} className="flex gap-2 items-center">
                      <Input value={v.label} onChange={e => updateVariant(idx, 'label', e.target.value)}
                        placeholder="100gr, Sucuklu, Bol Ekmek..." className="flex-1 h-8 text-sm" data-testid={`variant-label-${idx}`} />
                      <Input type="number" step="0.01" value={v.price} onChange={e => updateVariant(idx, 'price', e.target.value)}
                        placeholder="TL" className="w-24 h-8 text-sm" data-testid={`variant-price-${idx}`} />
                      <button type="button" onClick={() => removeVariant(idx)} className="text-red-500 hover:text-red-700 p-1">
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Fiyat - sadece seçenek yoksa göster */}
            {menuForm.variants.length === 0 && (
              <div className="space-y-1">
                <Label>Fiyat (TL) *</Label>
                <Input type="number" step="0.01" value={menuForm.price}
                  onChange={e => setMenuForm({...menuForm, price: e.target.value})} data-testid="menu-item-price" />
              </div>
            )}

            {/* Gorsel */}
            <div className="space-y-1">
              <Label>{`Görsel`}</Label>
              <Input type="file" accept="image/*" onChange={handleImageUpload} className="text-xs" data-testid="menu-image-input" />
              {menuForm.image && (
                <div className="mt-2 relative w-24 h-24 rounded-lg overflow-hidden border">
                  <img src={menuForm.image} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => setMenuForm({...menuForm, image: ''})}
                    className="absolute top-0 right-0 bg-red-500 text-white rounded-bl p-0.5">
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              <input type="checkbox" checked={menuForm.is_active}
                onChange={e => setMenuForm({...menuForm, is_active: e.target.checked})} id="is_active" />
              <Label htmlFor="is_active">Aktif</Label>
            </div>

            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowMenuDialog(false)} className="flex-1">{`İptal`}</Button>
              <Button type="submit" disabled={saving} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="save-menu-item-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Kaydet'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
